import type { Article } from '@wix/answers-api';
import {
  helpCenterModals,
  pageClick,
  wasItHelpfulView,
} from '@wix/bi-logger-customer-care-data/v2';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';

import { BI } from '../constants';
import { mapPageTypeToSourceName } from './bi';
import { useBI } from '../hooks/useBI';
import { PageType } from '../types';
import { useStore } from '../zustand/stores';

type SitePickerModalParams = {
  msid?: string;
  count: number;
  modalSessionId: string;
};

export const useArticleUnifiedBi = (article: Article) => {
  const { sendBIEvent } = useBI();
  const { locale } = useRouter();
  const { sessionBI } = useStore((state) => state.sessionSlice);

  const sourceName = useMemo(
    () => mapPageTypeToSourceName(PageType.Article),
    [],
  );
  const articleId = article.id;
  const selfServiceSessionId = sessionBI?.selfServiceSessionId || undefined;

  const sendPageClick = useCallback(
    (clickedItemType: string, extra?: Record<string, unknown>) =>
      sendBIEvent(
        pageClick({
          source_name: sourceName,
          item_id: articleId,
          clicked_item_type: clickedItemType,
          kb_lang: locale,
          self_service_session_id: selfServiceSessionId,
          is_page_content_click: false,
          ...extra,
        }),
      ),
    [sendBIEvent, sourceName, articleId, locale, selfServiceSessionId],
  );

  const inputFieldOpenTrack = useCallback(
    () => sendPageClick(BI.CLICKED_ITEM_TYPES.INPUT_FIELD_OPEN),
    [sendPageClick],
  );

  const inputFieldTextSubmitTrack = useCallback(
    (text: string) =>
      sendPageClick(BI.CLICKED_ITEM_TYPES.INPUT_FIELD_TEXT_SUBMIT, {
        clicked_text: text,
      }),
    [sendPageClick],
  );

  const inputFieldSuggestedQuestionTrack = useCallback(
    (text: string, order: number) =>
      sendPageClick(BI.CLICKED_ITEM_TYPES.INPUT_FIELD_SUGGESTED_QUESTION_CLICK, {
        clicked_text: text,
        clicked_item_order: order.toString(),
      }),
    [sendPageClick],
  );

  const helpmatePanelOpenCloseTrack = useCallback(
    (isOpen: boolean) =>
      sendPageClick(
        isOpen
          ? BI.CLICKED_ITEM_TYPES.HELPMATE_PANEL_OPEN
          : BI.CLICKED_ITEM_TYPES.HELPMATE_PANEL_CLOSE,
      ),
    [sendPageClick],
  );

  const helpmatePanelMinMaxTrack = useCallback(
    (isMinimize: boolean) =>
      sendPageClick(
        isMinimize
          ? BI.CLICKED_ITEM_TYPES.HELPMATE_PANEL_MINIMIZE
          : BI.CLICKED_ITEM_TYPES.HELPMATE_PANEL_MAXIMIZE,
      ),
    [sendPageClick],
  );

  const sitePickerRemoveTrack = useCallback(
    () => sendPageClick(BI.CLICKED_ITEM_TYPES.SITE_PICKER_REMOVE_SITE),
    [sendPageClick],
  );

  const sitePickerVisibleTrack = useCallback(
    (presentedMsid?: string) => {
      const event = wasItHelpfulView({
        source_name: sourceName,
        item_id: articleId,
        item_name: BI.ITEM_NAMES.SITE_PICKER,
        kb_lang: locale,
        self_service_session_id: selfServiceSessionId,
      });
      (event.params as Record<string, unknown>).presented_content = presentedMsid;
      return sendBIEvent(event);
    },
    [sendBIEvent, sourceName, articleId, locale, selfServiceSessionId],
  );

  const sharedModalParams = useMemo(
    () => ({
      modal_name: BI.MODAL_NAME.SITE_PICKER,
      source_name: sourceName,
      screen_name: BI.SCREEN_NAME.MAIN,
      origin: BI.ORIGINS.ARTICLE,
      item_id: articleId,
      kb_lang: locale,
      self_service_session_id: selfServiceSessionId,
    }),
    [sourceName, articleId, locale, selfServiceSessionId],
  );

  const sitePickerModalLoadTrack = useCallback(
    ({ msid, count, modalSessionId }: SitePickerModalParams) =>
      sendBIEvent(
        helpCenterModals({
          ...sharedModalParams,
          modal_session_id: modalSessionId,
          action_type: 'load' as const,
          params: JSON.stringify({ MSID: msid, count }),
        }),
      ),
    [sendBIEvent, sharedModalParams],
  );

  const sitePickerModalClickTrack = useCallback(
    (
      clickedMsid: string | undefined,
      previouslySelectedMsid: string | undefined,
      modalSessionId: string,
    ) =>
      sendBIEvent(
        helpCenterModals({
          ...sharedModalParams,
          modal_session_id: modalSessionId,
          action_type: BI.ACTION_TYPE.MODAL_CLICK,
          clicked_button_name: BI.CLICKED_ITEM_TYPES.SPECIFIC_SITE,
          clicked_item_id: clickedMsid,
          params: previouslySelectedMsid,
        }),
      ),
    [sendBIEvent, sharedModalParams],
  );

  return {
    inputFieldOpenTrack,
    inputFieldTextSubmitTrack,
    inputFieldSuggestedQuestionTrack,
    helpmatePanelOpenCloseTrack,
    helpmatePanelMinMaxTrack,
    sitePickerRemoveTrack,
    sitePickerVisibleTrack,
    sitePickerModalLoadTrack,
    sitePickerModalClickTrack,
  };
};
